
import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import ActionButton from 'components/buttons/action-button/ActionButton';

import SOVQueriesModalCollabary from '../collabary/modals/SOVQueriesModalCollabary';
import SOVQueriesModalConsumerInsights from '../consumer-insights/modals/SOVQueriesModalConsumerInsights';

import { resetSearchResults } from 'modules/insights/sov/reducers/queries/collabary/search-results/sov-queries-search-results';

import { __t, applyTranslations } from 'utils/language';
import { YOUTUBE } from 'modules/insights/sov/utils/sov';
import { CONSUMER_INSIGHTS } from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';
import {
  ALL,
  HASHTAGS,
  MULTI,
  PRIMARY,
  SECONDARY,
  SINGLE,
  STEPS,
  STEP_SEARCH_RESULTS
} from 'modules/insights/sov/utils/queries/sov-queries';

import type {
  SOVGeneralActionType,
  SOVGeneralModalTextFields,
  SOVGeneralPageType,
  SOVQueryLocal,
  SOVQuerySave,
  SOVQueryStep,
  SOVQueryStepType,
  SOVQueryType,
  SOVReport
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  report: SOVReport,
  query: SOVQueryLocal,
  pageType: SOVGeneralPageType,
  actionType: SOVGeneralActionType,
  onSaveQuery: (a: SOVQuerySave) => void,
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchResetSearchResults: () => void
};

type Props = OwnProps & DispatchProps;

type State = {
  isMulti: boolean,
  stepIndex: number,
  stepCount: number,
  stepDirection: number,
  activeTab: string,
  textFields: SOVGeneralModalTextFields
};

const STEP_DIRECTION = 'step-direction';
const COLLABARY_MSG_KEY = 'collabary_common';

class SOVQueriesModal extends React.PureComponent<Props, State> {
  state = {
    isMulti: this.props.query.type === MULTI,
    stepIndex: 0,
    stepCount: 0,
    stepDirection: 0,
    activeTab: HASHTAGS,
    textFields: {
      single: '',
      primary: '',
      secondary: ''
    }
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      ...this.state,
      stepCount: this.getTotalStepCount()
    };
  }

  componentDidMount() {
    const { query } = this.props;

    if (!isEmpty(query)) {
      this.handlePopulateTextFields();
    }
  }

  componentWillUnmount() {
    const { dispatchResetSearchResults } = this.props;

    dispatchResetSearchResults();
  }

  getStepsSingle(): Array<SOVQueryStep> {
    return STEPS.filter(item => item.queryType === ALL);
  }

  getTotalStepCount = (): number => {
    const { report } = this.props;
    const { isMulti } = this.state;

    if (report.reportType === YOUTUBE) {
      return 1;
    }

    if (isMulti) {
      return STEPS.length;
    }

    return this.getStepsSingle().length;
  };

  getStepInfo(): SOVQueryStep {
    const { stepIndex } = this.state;

    return STEPS[stepIndex];
  }

  getShowBackButtonStatus(): boolean {
    return this.state.stepIndex > 0;
  }

  getShowCancelButtonStatus(): boolean {
    return this.state.stepIndex === 0;
  }

  handlePopulateTextFields = (): void => {
    const { query } = this.props;
    const { isMulti } = this.state;

    this.handleTextFieldChange(SINGLE, !isMulti ? query.name : '');
    this.handleTextFieldChange(PRIMARY, isMulti && query.names ? query.names.primary : '');
    this.handleTextFieldChange(SECONDARY, isMulti && query.names ? query.names.secondary : '');
  };

  handleTabsSwitch = (identifier?: string | null): void => {
    this.setState({
      activeTab: identifier || HASHTAGS
    });
  };

  handleTextFieldChange = (fieldType: SOVQueryStepType, value: string): void => {
    this.setState(state => ({
      textFields: {
        ...state.textFields,
        [String(fieldType)]: value
      }
    }));
  };

  handleRadioButtonChange = (value: SOVQueryType): void => {
    this.setState({
      isMulti: value === MULTI
    }, () => {
      this.setState({
        stepCount: this.getTotalStepCount()
      });
    });
  };

  handleShowStep = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const stepDirection = Number(target.dataset.stepDirection) || 0;

    this.setState(state => {
      const stepIndex = state.stepIndex + stepDirection;
      const isSearchResultsStep = STEPS[stepIndex].key === STEP_SEARCH_RESULTS;

      return {
        ...state,
        stepIndex,
        stepDirection,
        ...(stepDirection !== -1 && isSearchResultsStep ? { activeTab: HASHTAGS } : null)
      };
    });
  };

  handleSaveQuery = (): void => {
    const { query, onSaveQuery, onCloseModal } = this.props;
    const { isMulti, textFields } = this.state;

    onSaveQuery({ query, textFields, isMulti });
    onCloseModal();
  };

  renderCancelButton = (): React.ReactNode | undefined | null => {
    const { onCloseModal } = this.props;
    const showButton = this.getShowCancelButtonStatus();

    if (!showButton) {
      return null;
    }

    return (
      <ActionButton
        color="tertiary"
        onClick={onCloseModal}
      >
        {__t(`${COLLABARY_MSG_KEY}.action_button.cancel`)}
      </ActionButton>
    );
  };

  renderBackButton = (): React.ReactNode | undefined | null => {
    const showButton = this.getShowBackButtonStatus();

    if (!showButton) {
      return null;
    }

    return (
      <ActionButton
        color="tertiary"
        data={{ [STEP_DIRECTION]: -1 }}
        onClick={this.handleShowStep}
      >
        {__t(`${COLLABARY_MSG_KEY}.action_button.back`)}
      </ActionButton>
    );
  };

  renderActionButton = (isDisabled: boolean): JSX.Element => {
    const { stepIndex } = this.state;
    const isLastSlide = stepIndex === this.getTotalStepCount() - 1;
    const buttonText = __t(`${COLLABARY_MSG_KEY}.action_button.${isLastSlide ? 'save' : 'next'}`);

    return (
      <ActionButton
        disabled={isDisabled}
        data={{ [STEP_DIRECTION]: 1 }}
        onClick={isLastSlide ? this.handleSaveQuery : this.handleShowStep}
      >
        {buttonText}
      </ActionButton>
    );
  };

  renderConsumerInsights(): React.ReactNode {
    const { report, query, actionType } = this.props;
    const { isMulti, activeTab, stepIndex, stepCount, stepDirection, textFields } = this.state;

    return (
      <div className="sov-queries-modal__consumer-insights">
        <SOVQueriesModalConsumerInsights
          report={report}
          query={query}
          isMulti={isMulti}
          actionType={actionType}
          activeTab={activeTab}
          stepIndex={stepIndex}
          stepCount={stepCount}
          stepDirection={stepDirection}
          stepInfo={this.getStepInfo()}
          textFields={textFields}
          onRenderActionButton={this.renderActionButton}
          onRenderCancelButton={this.renderCancelButton}
          onRenderBackButton={this.renderBackButton}
          onTextFieldChange={this.handleTextFieldChange}
          onRadioButtonChange={this.handleRadioButtonChange}
          onTabsSwitch={this.handleTabsSwitch}
        />
      </div>
    );
  }

  renderCollabary(): React.ReactNode {
    const { report, query, actionType } = this.props;
    const { isMulti, activeTab, stepIndex, stepCount, stepDirection, textFields } = this.state;

    return (
      <div className="sov-queries-modal__collabary">
        <SOVQueriesModalCollabary
          report={report}
          query={query}
          isMulti={isMulti}
          actionType={actionType}
          activeTab={activeTab}
          stepIndex={stepIndex}
          stepCount={stepCount}
          stepDirection={stepDirection}
          stepInfo={this.getStepInfo()}
          textFields={textFields}
          onRenderActionButton={this.renderActionButton}
          onRenderCancelButton={this.renderCancelButton}
          onRenderBackButton={this.renderBackButton}
          onTextFieldChange={this.handleTextFieldChange}
          onRadioButtonChange={this.handleRadioButtonChange}
          onTabsSwitch={this.handleTabsSwitch}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { pageType } = this.props;

    return (
      <div className="sov-queries-modal">
        {
          pageType === CONSUMER_INSIGHTS
            ? this.renderConsumerInsights()
            : this.renderCollabary()
        }
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetSearchResults: () => (
    dispatch(resetSearchResults())
  )
});

export default connect(null, mapDispatchToProps)(SOVQueriesModal);

